import type { AnalyticsEventPayload, UIAnalyticsEvent } from '@atlaskit/analytics-next';

import type { ConversationAssistantUIModes } from './analytics-properties-context/types';

export const ALLOWED_AI_MATE_SOURCES = [
	'AIMate',
	'smart-create',
	'CSMAI',
	'CSMAIEmbed',
	'rovoServiceAgent',
	'solutionComposer',
] as const;

/**
 * Type derived from the allowed sources array
 */
export type AIMateAnalyticsSource = (typeof ALLOWED_AI_MATE_SOURCES)[number];

export type SourceAndAttributes = {
	source: AIMateAnalyticsSource;
	// eslint-disable-next-line @typescript-eslint/no-explicit-any
	attributes: Record<string, any>;
};

export type CitedProductList = {
	[productName: string]: number;
};
export type SourceProducts = {
	[productName: string]: number;
};
/**
 * AI MAU Analytics
 * https://hello.atlassian.net/wiki/spaces/CONVAI/pages/edit-v2/3892665561
 * https://hello.atlassian.net/wiki/spaces/AAI/pages/3770721410/Unified+event+Instrumentation+for+AI
 */
export type TrackAIEventTypes =
	| 'initiated'
	| 'viewed'
	| 'error'
	| 'dismissed'
	| 'actioned'
	| 'feedback'
	| 'adopted';
export type TrackAIEventPayload<TKey extends TrackAIEventTypes, TData = void> = {
	type: TKey;
	proactive?: boolean;
	userGenerated?: boolean;
	source?: string;
	subjectId?: string;
	messageId?: string;
	pageContext?: string;
	// eslint-disable-next-line @typescript-eslint/no-explicit-any
	[key: string]: any;
} & (TData extends void ? {} : TData);

export type TrackAIInitiated = TrackAIEventPayload<'initiated'>;
export type TrackAIViewed = TrackAIEventPayload<
	'viewed',
	{ citedProductList: CitedProductList; sourceProducts: SourceProducts }
>;
export type TrackAIError = TrackAIEventPayload<'error'>;
export type TrackAIDismissed = TrackAIEventPayload<'dismissed'>;
export type TrackAIActioned = TrackAIEventPayload<
	'actioned',
	{ actionResult: string; citedProduct?: string | null; sourceProduct?: string | null }
>;
export type TrackAIFeedback = TrackAIEventPayload<'feedback', { feedbackResult: 'up' | 'down' }>;

export type RovoModifiedObject = {
	objectType:
		| 'work-item'
		| 'page'
		| 'whiteboard'
		| 'slide'
		| 'database'
		| 'team'
		| 'project'
		| 'pull-request';
	objectId: string;
};

/** AI MAU event used to track objects modified by Rovo */
export type TrackAIAdopted = TrackAIEventPayload<
	'adopted',
	{ rovoModifiedObjects: RovoModifiedObject[]; actionKey: string }
>;

export type TrackAIEventPayloads =
	| TrackAIInitiated
	| TrackAIViewed
	| TrackAIError
	| TrackAIDismissed
	| TrackAIActioned
	| TrackAIFeedback
	| TrackAIAdopted;

export type AIMAUEventPayload = {
	actionSubject: string;
	action: string;
	actionSubjectId?: string;
	eventType: 'track';
	tags: string[];
	source?: string;
	attributes: {
		proactiveAIGenerated: 1 | 0;
		userGeneratedAI: 1 | 0;
		isAIFeature: 1;
		aiFeedbackResult?: 'up' | 'down';
		aiResultAction?: string;
		aiFeatureName: 'aiChat';
		messageId?: string;
		// eslint-disable-next-line @typescript-eslint/no-explicit-any
		[key: string]: any;
	};
};

export type SendAnalyticsEvent = (
	payload: AnalyticsEventPayload | UIAnalyticsEvent,
	options?: { removeDefaultTags?: boolean },
) => void;

export type IUseAnalytics = {
	sendAnalyticsEvent: SendAnalyticsEvent;
	trackAIMAUEvent: (event: TrackAIEventPayloads) => void;
	mode: ConversationAssistantUIModes;
};
